import { uid } from 'uid'
import { getDefaultFilterDefinitions } from 'webgl-effects'

import { useChangeCounter } from 'shared/utils/composables.ts'

import type * as pub from '../../types/core.d.ts'
import type { Schema } from '../../types/core.d.ts'
import { AssetCreateEvent, AssetDeleteEvent } from '../events.ts'
import { FileSystemStorage } from '../storage/file-system-storage.ts'

import { FontAsset } from './font-asset.ts'
import { HttpAssetLoader } from './http-asset-loader.ts'
import { ImageAsset, MediaAsset } from './media-asset.ts'
import { getMediaAssetInfo } from './utils.ts'
import { VideoEffectAsset } from './video-effect-asset.ts'

export class FileSystemAssetStore extends EventTarget implements pub.VideoEditorAssetStore {
  readonly #map = new Map<string, pub.AnyAsset>()
  readonly #abort = new AbortController()
  isDisposed = false
  fileStorage = new FileSystemStorage()
  loaders: pub.AssetLoader[] = [new HttpAssetLoader()]
  protected generateId: () => string
  protected getMediaAssetInfo = getMediaAssetInfo

  readonly #changeCounter = useChangeCounter()

  constructor(generateId: () => string = uid) {
    super()
    this.generateId = generateId

    getDefaultFilterDefinitions().forEach((def) => {
      this.create({ ...def, id: `filter:${def.id!}`, type: 'asset:effect:video' }, { isBuiltIn: true })
    })

    this.on('asset:create', this.#changeCounter.inc)
    this.on('asset:delete', this.#changeCounter.inc)
  }

  values(): MapIterator<pub.AnyAsset> {
    this.#changeCounter.track()
    return this.#map.values()
  }

  has(id: string): boolean {
    this.#changeCounter.track()
    return this.#map.has(id)
  }

  create<T extends Schema.AnyAsset>(
    init: T,
    { source, isBuiltIn }: { source?: Blob | string; isBuiltIn?: boolean } = {},
  ): pub.AssetsByType[T['type']] {
    let asset

    switch (init.type) {
      case 'asset:media:image':
      case 'asset:media:av': {
        const options = { store: this, source, isBuiltIn }
        asset = init.type === 'asset:media:av' ? new MediaAsset(init, options) : new ImageAsset(init, options)
        this.getOrCreateFile(asset, source ?? ('uri' in init ? init.uri : undefined))
          .then(asset.setBlob.bind(asset))
          .catch(asset.setError.bind(asset))
        break
      }

      case 'asset:font':
        asset = new FontAsset(init, this, isBuiltIn)
        break

      case 'asset:effect:video':
        asset = new VideoEffectAsset(init, this, isBuiltIn)
        break
    }

    this.#map.set(asset.id, asset)
    this.#emit(new AssetCreateEvent(asset))

    return asset as unknown as pub.AssetsByType[T['type']]
  }

  getAsset<T extends pub.AnyAsset | undefined>(id: string): T {
    this.#changeCounter.track()
    return this.#map.get(id) as T
  }

  async createFile(
    asset: pub.MediaAsset,
    stream: ReadableStream<Uint8Array>,
    options: { onProgress?: (progress: number) => void; signal?: AbortSignal | null },
  ): Promise<void> {
    await this.fileStorage.create(asset.id, stream, { ...options, size: asset.size })
  }

  async getFile(key: string, name?: string, options?: FilePropertyBag): Promise<File> {
    return await this.fileStorage.get(key, name, options)
  }

  async getOrCreateFile(
    asset: pub.BaseFileAsset,
    source_: Blob | string | undefined,
    options?: { signal?: AbortSignal | null },
  ): Promise<File> {
    const storageHasFile = await this.fileStorage.hasCompleteFile(asset.id)
    let source = source_

    if (!storageHasFile) {
      source ??= asset.uri
      if (source == null) throw new Error('[webgl-video-editor] Missing file blob or uri.')

      let stream
      let size

      if (typeof source === 'string') {
        const loader = this.loaders.find((l) => l.canLoad(asset))

        if (!loader) throw new Error('[webgl-video-editor] Unable to load asset.')
        ;({ stream, size } = await loader.load(asset, options))
      } else {
        stream = source.stream()
        ;({ size } = source)
      }

      await this.fileStorage.create(asset.id, stream, { ...options, size })
    }

    return await this.fileStorage.get(asset.id, asset.name, { type: asset.mimeType })
  }

  async delete(key: string): Promise<void> {
    const asset = this.getAsset(key)
    await this.fileStorage.delete(key)
    asset?.dispose()
    this.#map.delete(key)
    if (asset) this.#emit(new AssetDeleteEvent(asset))
  }

  async createMediaAsset(source: Blob | string): Promise<pub.MediaAsset | pub.ImageAsset> {
    const init = await getMediaAssetInfo(this.generateId(), source)
    return this.create(init, { source })
  }

  #emit(event: pub.VideoEditorEvents[pub.AssetEventType]): void {
    this.dispatchEvent(event)
  }

  on<T extends pub.AssetEventType>(
    type: T,
    listener: (event: pub.VideoEditorEvents[T]) => void,
    options_?: AddEventListenerOptions,
  ) {
    const options = { signal: this.#abort.signal, ...options_ }
    this.addEventListener(type, listener as EventListener, options)
    return () => this.removeEventListener(type, listener as EventListener, options)
  }

  dispose(): void {
    this.isDisposed = true
    this.fileStorage.dispose()
    this.#abort.abort()

    this.#map.forEach((s) => s.dispose())
    this.#map.clear()
  }

  [Symbol.dispose](): void {
    this.dispose()
  }
}
